import { useSyncExternalStore } from "react"

const MOBILE_BREAKPOINT = 768

// Lazy `MediaQueryList` accessor — `window` is undefined during Vite SSR
// (Node.js context), so we can't construct `mql` at module-evaluation time.
// Each call from the hook re-resolves; browsers cache MediaQueryList objects
// internally, so the same query string returns the same underlying object.
function getMediaQueryList(): MediaQueryList | null {
  if (typeof window === "undefined") return null
  return window.matchMedia(`(max-width: ${MOBILE_BREAKPOINT - 1}px)`)
}

// SSR no-op — useSyncExternalStore demands a cleanup function from `subscribe`.
// During SSR there's nothing to unsubscribe from.
const noopUnsubscribe = () => {
  // intentionally empty
}

function mediaQueryListener(callback: () => void) {
  const mql = getMediaQueryList()
  if (!mql) return noopUnsubscribe
  mql.addEventListener("change", callback)

  return () => {
    mql.removeEventListener("change", callback)
  }
}

function isSmallerThanBreakpoint() {
  return getMediaQueryList()?.matches ?? false
}

// SSR snapshot — assume desktop. The first client render after hydration
// re-evaluates and updates if the actual viewport is mobile. (Causes a brief
// hydration mismatch warning if SSR'd HTML differs, which is acceptable for
// a viewport-conditional render — there is no "correct" SSR answer because
// the server can't know the client's viewport.)
function isSmallerThanBreakpointOnServer() {
  return false
}

export function useIsMobile() {
  return useSyncExternalStore(
    mediaQueryListener,
    isSmallerThanBreakpoint,
    isSmallerThanBreakpointOnServer,
  )
}
